import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Play, Pause, Waveform, ArrowRight } from "@phosphor-icons/react";
import { API } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";

export default function SharedSound() {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const { t } = useLang();
  const [sound, setSound] = useState(null);
  const [error, setError] = useState(false);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/share/${shareId}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setSound)
      .catch(() => setError(true));
  }, [shareId]);

  const togglePlay = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); }
    else { a.play(); setPlaying(true); }
  };

  const kindBadge = sound?.is_mix ? "MIX" : sound?.kind === "music" ? "MUSIC" : sound?.kind === "speech" ? "VOICE" : "SFX";

  return (
    <div className="min-h-screen relative overflow-hidden flex flex-col" data-testid="shared-sound-page">
      <nav className="relative z-10 flex items-center justify-between px-8 py-6 max-w-[1100px] mx-auto w-full">
        <div className="flex items-center gap-2.5 cursor-pointer" onClick={() => navigate("/")} data-testid="shared-logo">
          <div className="w-8 h-8 rounded-lg bg-[color:var(--accent)] grid place-items-center">
            <Waveform size={18} weight="fill" color="white" />
          </div>
          <span className="font-display font-bold text-xl tracking-tight">AuraFX</span>
        </div>
      </nav>

      <main className="relative z-10 flex-1 flex items-center justify-center px-6 pb-16">
        <div className="w-full max-w-xl">
          {error ? (
            <div className="text-center" data-testid="shared-not-found">
              <p className="text-white/60">404 — {t("shShared")}</p>
            </div>
          ) : !sound ? (
            <div className="text-center text-white/40 text-sm">…</div>
          ) : (
            <div className="glass rounded-2xl border border-white/10 p-8" data-testid="shared-sound-card">
              <audio
                ref={audioRef}
                src={`${API}/share/${shareId}/audio`}
                preload="auto"
                onEnded={() => setPlaying(false)}
              />
              <div className="flex items-center gap-2 text-[10px] font-mono-num uppercase tracking-widest text-white/40 mb-5">
                <span className="px-1.5 py-0.5 rounded-sm bg-[color:var(--accent)]/20 text-[color:var(--accent)]">{kindBadge}</span>
                {sound.category && sound.category.toLowerCase() !== kindBadge.toLowerCase() && <><span>·</span><span>{sound.category}</span></>}
                {sound.duration_seconds && <><span>·</span><span>{Number(sound.duration_seconds).toFixed(1)}s</span></>}
                <span className="ml-auto">{t("shShared")}</span>
              </div>
              <div className="flex items-center gap-5">
                <button
                  data-testid="shared-play-btn"
                  onClick={togglePlay}
                  className="shrink-0 w-16 h-16 rounded-full bg-[color:var(--accent)] hover:bg-[color:var(--accent-hover)] grid place-items-center transition-colors shadow-[0_0_30px_-5px_rgba(229,9,20,0.6)]"
                >
                  {playing ? <Pause size={26} weight="fill" color="white" /> : <Play size={26} weight="fill" color="white" />}
                </button>
                <div className="flex-1 min-w-0">
                  <p className="font-display font-semibold text-lg leading-snug" data-testid="shared-prompt">{sound.prompt}</p>
                  <div className={`mt-3 flex items-end gap-[2px] h-8 ${playing ? "wave-playing" : ""}`}>
                    {Array.from({ length: 40 }).map((_, i) => (
                      <span key={i} className="wave-bar w-[3px] bg-[color:var(--accent)] opacity-80 rounded-full"
                        style={{ height: `${20 + ((i * 7) % 80)}%`, animationDelay: `${(i * 60) % 800}ms` }} />
                    ))}
                  </div>
                </div>
              </div>
              <div className="mt-8 pt-6 border-t border-white/10 text-center">
                <p className="text-xs font-mono-num uppercase tracking-widest text-white/40">{t("shMadeWith")}</p>
                <p className="mt-2 text-sm text-white/70 max-w-sm mx-auto">{t("shCtaTitle")}</p>
                <button
                  data-testid="shared-cta-btn"
                  onClick={() => navigate("/")}
                  className="btn-primary inline-flex items-center gap-2 px-6 py-3 rounded-full font-medium mt-5"
                >
                  {t("shCtaBtn")} <ArrowRight size={16} weight="bold" />
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
